import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { TrendIndicator } from "../components/TrendIndicator";
import { Typography } from "antd";

interface TrendData {
  metric: string;
  trend: string;
  timeseries: number[];
  change_summary: string;
}

// Trend Block Recipe
const trendRecipe = {
  id: "trend-block",
  name: "Trend Block",
  globalKey: [],
  prefix: `[
        {
          "metric": "string — metric being tracked (e.g. 'PRs merged')",
          "trend": "string — 'up', 'down', 'flat', 'volatile'",
          "timeseries": ["number — one per time period"],
          "change_summary": "string — brief explanation (e.g. 'PRs fell due to infrastructure bugs.')"
        }
      ]`,
  format: {
    type: "json",
    fields: ["metric", "trend"],
    constraints: [
      { field: "metric", type: "string", required: true },
      { field: "trend", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as TrendData;
          return Boolean(parsed.metric && parsed.trend);
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// Trend Block Parser
function trendParser(data: any) {
  try {
    return {
      data: data[trendBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse trend data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// Trend Block Variants
const trendVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard trend display with chart",
    defaultSettings: {
      showChart: true,
      showSummary: true,
    },
  },
  {
    id: "compact",
    name: "Compact",
    description: "Minimal trend display without chart",
    defaultSettings: {
      showChart: false,
      showSummary: true,
    },
  },
  {
    id: "detailed",
    name: "Detailed",
    description: "Detailed trend display with annotations",
    defaultSettings: {
      showChart: true,
      showSummary: true,
      showAnnotations: true,
    },
  },
];

// Trend Block Renderer
function TrendBlockRenderer(props: any) {
  const { data, variant, settings, className, style } = props;
  const trendData = data[trendBlock.id];
  return (
    <>
      {trendData?.length > 0 && (
        <div className="vzbl-panel-container">
          <Typography.Title level={4} className="vzbl-header-4">
            Themes
          </Typography.Title>
          <div className="responsive-card-grid">
            {trendData.slice(0, 3).map((trend: TrendData, idx: number) => (
              <div key={idx} style={{ height: "100%" }}>
                <TrendIndicator
                  {...trendData}
                  variant={variant}
                  className={className}
                  style={{
                    ...style,
                    ...settings?.style,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// Create Trend Block
const trendBlock = createBaseBlock({
  id: "trend",
  name: "Trend Block",
  description: "Displays trend data with optional chart visualization",
  recipe: trendRecipe,
  variants: trendVariants,
  parser: trendParser,
  renderer: TrendBlockRenderer,
});

// Export the block for direct use
export { trendBlock };
